import {useEffect, useState} from "react";
import {readProgramRoadmap, type ProgramRoadmap as Roadmap} from "../../api/client";
import {useAuth} from "../../auth/AuthContext";
import {AsyncState} from "../../components/AsyncState";
import {StatusBadge} from "../../components/StatusBadge";

export function ProgramRoadmap({programId}: {programId: string}) {
  const {authorizedFetch} = useAuth();
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void readProgramRoadmap(authorizedFetch, programId, controller.signal)
      .then(value => { setRoadmap(value); setError(""); })
      .catch(problem => { if (!controller.signal.aborted) setError(message(problem)); });
    return () => controller.abort();
  }, [authorizedFetch, programId]);

  if (error) return <AsyncState kind="error" detail={error} />;
  if (!roadmap) return <AsyncState kind="loading" detail="Deriving roadmap state from governed Factory objects." />;
  return <section className="program-roadmap" aria-labelledby="roadmap-title">
    <header className="page-header">
      <div><p className="eyebrow">Program · read-only projection</p>
        <h1 id="roadmap-title">{roadmap.programKey} roadmap</h1>
        <p>{roadmap.programTitle}. Roadmap state is derived from governed Program, Work Package, Execution Plan, and milestone records; it is not editable roadmap truth.</p></div>
      <div className="roadmap-header-actions"><StatusBadge value={roadmap.programLifecycle} />
        <a className="button-link" href={`/programs/${roadmap.programId}`}>Program workspace</a></div>
    </header>
    <div className="roadmap-currentness" role="note">
      <strong>Current as of {formatDate(roadmap.currentAsOf)}</strong><span>{roadmap.scope}</span><small>{roadmap.currentness}</small>
    </div>
    {roadmap.milestones.length === 0 ? <AsyncState kind="empty" detail="This Program has no governed Work Packages or execution milestones to project yet." />
      : <ol className="roadmap-timeline" aria-label="Program milestones">
        {roadmap.milestones.map(item => <li key={`${item.sourceKind}-${item.sourceId}`} className={`roadmap-${item.status.toLowerCase()}`}>
          <div className="roadmap-sequence" aria-hidden="true">{item.sequence}</div>
          <article>
            <header><div><p className="eyebrow">{item.sourceKind.replaceAll("_", " ")}</p><h2>{item.name}</h2></div><StatusBadge value={item.status} /></header>
            <dl>
              <div><dt>Owner</dt><dd>{item.ownerLane || "Not recorded"}</dd></div>
              <div><dt>Dependency</dt><dd>{item.dependency || "None recorded"}</dd></div>
              <div><dt>Started</dt><dd>{formatDate(item.startedAt)}</dd></div>
              <div><dt>Completed</dt><dd>{formatDate(item.completedAt)}</dd></div>
            </dl>
            {item.blocker && <div className="roadmap-callout blocker"><strong>Blocker</strong><p>{item.blocker}</p></div>}
            {item.nextAction && <div className="roadmap-callout"><strong>Next action</strong><p>{item.nextAction}</p></div>}
            {item.acceptedIdentity && <p className="roadmap-identity"><strong>Accepted identity</strong><code>{item.acceptedIdentity}</code></p>}
            <nav aria-label={`${item.name} governing objects`} className="roadmap-links">
              {item.links.map(link => <a key={`${link.objectType}-${link.objectId}`} href={link.url}>{link.label}</a>)}
            </nav>
          </article>
        </li>)}
      </ol>}
  </section>;
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}
function message(problem: unknown) { return problem instanceof Error ? problem.message : "Program roadmap could not be loaded"; }
